/**
 * Pure model-package STRUCTURE planning.
 * Contract: docs/doar-core/DOARSPACE_UNIVERSAL_WORKSPACE_CONTRACT.md #6, #10.
 *
 * A 3D model is often not one file: an OBJ needs its MTL and textures, a glTF
 * needs its .bin and textures. This module answers only the structural
 * question - "which of these files belong together, and which file is the
 * root?" - so the same answer is used everywhere instead of each surface
 * re-deriving it (GraphicalSpace had one private copy, ModelSpace another).
 *
 * It deliberately does NOT decide file CAPABILITY. Whether a root is DIRECT,
 * REAL_CONVERSION, SOURCE_ONLY or REJECTED remains utils/fileTypeMap.js's
 * single authority, and what workspace/editor an uploaded Asset opens in
 * remains representationResolver's. The companion table below is the one
 * structural extension table this module is allowed to own.
 */

export const MODEL_ROOT_EXTENSIONS = Object.freeze(["obj", "gltf", "fbx", "glb"]);

// Companion files a given root may legitimately carry. These are package
// STRUCTURE rules, not capability rules.
const COMPANION_EXTENSIONS = Object.freeze({
  obj: new Set(["mtl", "png", "jpg", "jpeg", "tga", "bmp", "gif", "webp", "tiff", "tif"]),
  gltf: new Set(["bin", "png", "jpg", "jpeg", "webp", "tga", "bmp", "tiff"]),
  fbx: new Set(["png", "jpg", "jpeg", "tga", "bmp", "webp"]),
  glb: new Set(),
});

// Extensions that only ever exist to accompany a model root - they are never a
// workspace representation of their own, so they must never be promoted to a
// fabricated root Asset.
const COMPANION_ONLY_EXTENSIONS = Object.freeze(new Set(["mtl", "bin", "tga"]));

const ROOT_SET = new Set(MODEL_ROOT_EXTENSIONS);

/**
 * The accept fragment a package-capable file picker needs, DERIVED from the
 * companion table above rather than restated per surface.
 *
 * Without this, a native picker filtered only by standalone-capable types hides
 * the very files a package requires (.bin, .mtl, .tga), so a user physically
 * cannot select a complete glTF or OBJ package. Appearing here does NOT make a
 * companion a standalone Asset: planWorkspaceImport still refuses a selection of
 * companions with no root (ORPHAN_COMPANIONS).
 */
export const MODEL_PACKAGE_COMPANION_EXTENSIONS = Object.freeze(
  Array.from(new Set(Object.values(COMPANION_EXTENSIONS).flatMap((set) => Array.from(set)))).sort()
);

export const MODEL_PACKAGE_COMPANION_ACCEPT = MODEL_PACKAGE_COMPANION_EXTENSIONS
  .map((ext) => `.${ext}`)
  .join(",");

export function fileExtension(name) {
  const value = String(name || "");
  const dot = value.lastIndexOf(".");
  return dot === -1 ? "" : value.slice(dot + 1).toLowerCase();
}

export function isModelRootExtension(name) {
  return ROOT_SET.has(fileExtension(name));
}

export function isCompanionOnlyExtension(name) {
  return COMPANION_ONLY_EXTENSIONS.has(fileExtension(name));
}

/**
 * Which companions a root of this type accepts.
 * @returns {Set<string>} lowercase extensions, without dots.
 */
export function companionExtensionsFor(name) {
  return COMPANION_EXTENSIONS[fileExtension(name)] || new Set();
}

/**
 * Groups a flat file list into logical imports.
 *
 * Grouping only happens when exactly ONE model root is present - with several
 * roots there is no way to know which textures belong to which model, so each
 * root stays its own logical import and nothing is silently mis-attached.
 *
 * @param {File[]|FileList} files
 * @returns {Array<{ rootFile: File, companions: File[] }>}
 */
export function planModelPackages(files) {
  const all = Array.from(files || []);
  const roots = all.filter((file) => isModelRootExtension(file?.name));

  if (!roots.length) return all.map((file) => ({ rootFile: file, companions: [] }));

  if (roots.length === 1) {
    const root = roots[0];
    const allowed = companionExtensionsFor(root.name);
    const companions = all.filter((file) => file !== root && allowed.has(fileExtension(file?.name)));
    const companionSet = new Set(companions);
    const standalone = all.filter((file) => file !== root && !companionSet.has(file));
    return [
      { rootFile: root, companions },
      ...standalone.map((file) => ({ rootFile: file, companions: [] })),
    ];
  }

  // Several roots: no auto-grouping at all.
  return all.map((file) => ({ rootFile: file, companions: [] }));
}

export const IMPORT_PLAN_KIND = Object.freeze({
  /** Exactly one ordinary file. */
  SINGLE_FILE: "single-file",
  /** One model root plus its companions. */
  MODEL_PACKAGE: "model-package",
  /** Several independent logical imports - the caller must narrow the choice. */
  MULTIPLE_ROOTS: "multiple-roots",
  /** Only companion-only files, with no root to attach them to. */
  ORPHAN_COMPANIONS: "orphan-companions",
  /** Nothing usable was provided. */
  EMPTY: "empty",
});

/**
 * Classifies a selection into ONE logical workspace import, for surfaces that
 * accept a single import at a time (the canonical root intake). Structural
 * only - it never says whether the root's format is supported.
 *
 * @param {File[]|FileList} files
 * @returns {{ kind: string, rootFile: File|null, companions: File[], packages: Array, reason: string|null }}
 */
export function planWorkspaceImport(files) {
  const all = Array.from(files || []);
  if (!all.length) {
    return { kind: IMPORT_PLAN_KIND.EMPTY, rootFile: null, companions: [], packages: [], reason: "no_files" };
  }

  const packages = planModelPackages(all);
  if (all.every(file => isCompanionOnlyExtension(file?.name))) return { kind:IMPORT_PLAN_KIND.ORPHAN_COMPANIONS,rootFile:null,companions:all,packages,reason:'orphan_companions' };

  if (packages.length > 1) {
    return {
      kind: IMPORT_PLAN_KIND.MULTIPLE_ROOTS,
      rootFile: null,
      companions: [],
      packages,
      reason: "multiple_logical_imports",
    };
  }

  const [only] = packages;
  // A selection of nothing but companion-only files is not a model package and
  // must never be uploaded as an arbitrary root.
  if (isCompanionOnlyExtension(only.rootFile?.name)) {
    return {
      kind: IMPORT_PLAN_KIND.ORPHAN_COMPANIONS,
      rootFile: null,
      companions: all,
      packages,
      reason: "orphan_companions",
    };
  }

  return {
    kind: only.companions.length ? IMPORT_PLAN_KIND.MODEL_PACKAGE : IMPORT_PLAN_KIND.SINGLE_FILE,
    rootFile: only.rootFile,
    companions: only.companions,
    packages,
    reason: null,
  };
}
